interface ConfidenceIndicatorProps {
  /** Confidence score as a percentage (0-100) or null/undefined if not available */
  confidence: number | null | undefined;
  /** Whether the document is still being processed */
  isProcessing?: boolean;
  /** Whether to show the compact version (for table cells) */
  compact?: boolean;
}

/**
 * Determines the color classes based on confidence score
 * - Green: >85% - High confidence, likely accurate
 * - Yellow: 70-85% - Medium confidence, may need review
 * - Red: <70% - Low confidence, needs attention
 */
function getConfidenceColors(confidence: number): {
  bgColor: string;
  textColor: string;
  barColor: string;
} {
  if (confidence > 85) {
    return {
      bgColor: "bg-green-100 dark:bg-green-950/30",
      textColor: "text-green-700 dark:text-green-400",
      barColor: "bg-green-500 dark:bg-green-400",
    };
  }
  if (confidence >= 70) {
    return {
      bgColor: "bg-yellow-100 dark:bg-yellow-950/30",
      textColor: "text-yellow-700 dark:text-yellow-400",
      barColor: "bg-yellow-500 dark:bg-yellow-400",
    };
  }
  return {
    bgColor: "bg-red-100 dark:bg-red-950/30",
    textColor: "text-red-700 dark:text-red-400",
    barColor: "bg-red-500 dark:bg-red-400",
  };
}

/**
 * ConfidenceIndicator component - Displays extraction confidence score
 *
 * Used in the document table to show how confident the extraction was.
 * Shows "Processing..." for documents still being processed.
 *
 * Color coding:
 * - Green (>85%): High confidence
 * - Yellow (70-85%): Medium confidence
 * - Red (<70%): Low confidence
 */
export function ConfidenceIndicator({
  confidence,
  isProcessing = false,
  compact = false,
}: ConfidenceIndicatorProps) {
  // Show processing state only when actively processing
  if (isProcessing) {
    return (
      <div
        className={`
          inline-flex items-center gap-1.5
          ${compact ? "px-2 py-0.5" : "px-3 py-1"}
          bg-gray-100 dark:bg-gray-800/50 
          rounded-full
        `}
      >
        {/* Animated spinner */}
        <span
          className={`
            material-symbols-outlined 
            text-gray-500 dark:text-gray-400 
            animate-spin
            ${compact ? "text-sm" : "text-base"}
          `}
        >
          progress_activity
        </span>
        <span
          className={`
            text-gray-600 dark:text-gray-400 
            ${compact ? "text-xs" : "text-sm"} 
            font-medium
          `}
        >
          Processing...
        </span>
      </div>
    );
  }

  // Show N/A when confidence is not available (no extraction data)
  if (confidence === null || confidence === undefined) {
    return (
      <div
        className={`
          inline-flex items-center gap-1.5
          ${compact ? "px-2 py-0.5" : "px-3 py-1"}
          bg-gray-100 dark:bg-gray-800/50 
          rounded-full
        `}
      >
        <span
          className={`
            text-gray-500 dark:text-gray-400 
            ${compact ? "text-xs" : "text-sm"} 
            font-medium
          `}
        >
          N/A
        </span>
      </div>
    );
  }

  // Clamp confidence to 0-100 range
  const clampedConfidence = Math.max(0, Math.min(100, confidence));
  const { bgColor, textColor, barColor } = getConfidenceColors(clampedConfidence);

  // Compact version for table cells
  if (compact) {
    return (
      <div className="flex items-center gap-2 min-w-[80px]">
        {/* Mini progress bar */}
        <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full ${barColor} transition-all duration-300`}
            style={{ width: `${clampedConfidence}%` }}
          />
        </div>
        {/* Percentage text */}
        <span className={`text-xs font-medium ${textColor} tabular-nums w-8 text-right`}>
          {Math.round(clampedConfidence)}%
        </span>
      </div>
    );
  }

  // Full version with badge and progress bar
  return (
    <div className="flex flex-col gap-1.5 min-w-[120px]">
      {/* Badge with percentage */}
      <div
        className={`
          inline-flex items-center gap-1.5 
          px-2 py-0.5 
          ${bgColor} 
          rounded-full 
          w-fit
        `}
      >
        <span className={`text-sm font-semibold ${textColor} tabular-nums`}>
          {Math.round(clampedConfidence)}%
        </span>
        {/* Confidence level icon */}
        <span className={`material-symbols-outlined text-sm ${textColor}`}>
          {clampedConfidence > 85 ? "check_circle" : clampedConfidence >= 70 ? "warning" : "error"}
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-300 ease-out`}
          style={{ width: `${clampedConfidence}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Helper to get confidence color class name for external use
 * Useful for sorting or filtering by confidence level
 */
export function getConfidenceLevel(
  confidence: number | null
): "high" | "medium" | "low" | "unknown" {
  if (confidence === null) return "unknown";
  if (confidence > 85) return "high";
  if (confidence >= 70) return "medium";
  return "low";
}

/**
 * Helper to get sort priority for confidence levels
 * Lower numbers = higher priority (worse confidence first)
 */
export function getConfidenceSortPriority(confidence: number | null): number {
  if (confidence === null) return 0; // Processing items first
  return confidence;
}
